import React, { useRef, useState, useEffect, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { withApollo } from 'react-apollo';
import { compose } from 'recompose';

import { useConfig } from 'Context/ConfigContext';
import { Box, Flex, Text, SvgIcon } from 'Components/Base';
import { IS_SHOW_MY_COURSE_NAVIGATION_ARROW } from 'Util/Capacitors/constant';
import { useCourseFilter } from 'Util/Hooks';
import { MY_COURSES_MODE } from 'Domain/MyCourseData';
import { useSearchMyCourseProvider } from 'Context/SearchMyCourseProvider';

import { QueryComponent } from '../../../../GraphQL';
import { myCourses } from '../../../../GraphQL/query/Product.query';
import { syncMyCourses } from '../../../../GraphQL/mutation/Student.mutation';
import {
  sortCoursesByDate,
  sortCoursesByName,
  getAvailableCourses,
  sortCoursesByProgress,
} from '../../../../Util/course';
import Loading from '../../../../Components/Loading';
import { useMyCoursesTracking } from '../../../../Util/Analytics';

import { getFilteredItems, getFilterFromArray } from './utils';
import CourseListView from './CourseList.view';

const ArrowButton = styled((props) => (
  <Flex
    width={'50px'}
    height={'49px'}
    alignItems={'center'}
    justifyContent={'center'}
    bg={'white'}
    {...props}
  />
))`
  position: absolute;

  ${({ isLeft }) =>
    isLeft
      ? css`
          left: 0;
          box-shadow: 2px 0px 4px rgba(0, 0, 0, 0.1);
        `
      : css`
          right: 0;
          box-shadow: -2px 0px 4px rgba(0, 0, 0, 0.1);
        `};
  top: 0;
  cursor: pointer;
  z-index: 1;
`;

const SubjectItem = styled((props) => (
  <Flex height={'100%'} px={4} justifyContent={'center'} alignItems={'center'} {...props} />
))`
  cursor: pointer;
  border-bottom: 2px solid transparent;
  ${({ active }) =>
    active &&
    css`
      border-color: ${({ theme }) => theme.colors.primary};
    `}
`;

const SubjectWithRef = forwardRef((props, ref) => (
  <Box ref={ref} style={{ flexShrink: 0 }}>
    <SubjectItem {...props} />
  </Box>
));

SubjectWithRef.displayName = 'SubjectWithRef';

const SubjectListContainer = styled((props) => <Flex width={'100%'} height={'50px'} {...props} />)`
  position: relative;
  box-shadow: inset 0 -1px 0 0 #e5e5e5;
  overflow: hidden;
  * {
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

const SCROLL_STATE = {
  START: 'START',
  END: 'END',
  MIDDLE: 'MIDDLE',
};

const DEFAULT_SUBJECT = 'my_course';

const CourseList = ({ client, i18n, status, t, onCourseCardClick, ...props }) => {
  const { disableSyncMyCourses, subjectList, myCourses: myCoursesConfig } = useConfig();
  const { logSubjectSelect, logFilterChange } = useMyCoursesTracking();
  const [isSync, setIsSync] = useState(false);
  const [currentSubject, setCurrentSubject] = useState(DEFAULT_SUBJECT);
  const { filter, options, onFilterChange, COURSE_FILTER, SORT_FILTER } = useCourseFilter();

  const [scrollState, setScrollState] = useState(SCROLL_STATE.START);

  const subjectRef = useRef([]);
  const subjectListRef = useRef();
  const showArrow = IS_SHOW_MY_COURSE_NAVIGATION_ARROW;

  /**
   * @type {{[key:string]:string}} Subject key and subject description object mapping
   */
  const subjects = {
    [DEFAULT_SUBJECT]: 'คอร์สของฉัน',
  };
  for (const subject of subjectList) {
    subjects[subject.key] = subject.description;
  }

  /**
   * @type {[string]} array of subject key
   */
  const subjectKeys = subjectList.map((subject) => subject.key);

  useEffect(() => {
    if (disableSyncMyCourses && !isSync) {
      setIsSync(true);
    } else {
      if (!isSync) {
        client
          .mutate({
            mutation: syncMyCourses,
            variables: { body: {} },
          })
          .then(() => setIsSync(true))
          .catch((err) => {
            console.error(err);
            setIsSync(true);
          });
      }
    }
  }, []);

  const setSubjectList = (courses) => {
    const subjectList = new Set();
    subjectList.add(DEFAULT_SUBJECT);
    courses.forEach((course) => {
      if (subjectKeys.includes(course.courseInfo.courseLevel)) {
        subjectList.add(course.courseInfo.courseLevel);
      }
    });
    // order subjects to display according to the reference SUBJECT_ORDER
    return Array.from(subjectList).sort((a, b) => {
      return subjectKeys.indexOf(a) - subjectKeys.indexOf(b);
    });
  };

  const getCurrentSubjectCourses = (courses) => {
    if (currentSubject === DEFAULT_SUBJECT) return courses;
    return courses.filter((course) => {
      return course.courseInfo.courseLevel === currentSubject;
    });
  };

  const filterCourses = (courses) => {
    let filteredCourses;
    if (Object.values(COURSE_FILTER).includes(filter.courses)) {
      const isExpired = filter.courses === COURSE_FILTER.EXPIRED_COURSES;
      filteredCourses = getAvailableCourses(courses, isExpired);
    } else {
      filteredCourses = courses;
    }

    switch (filter.sort) {
      case SORT_FILTER.LATEST:
      case SORT_FILTER.OLDEST:
        return sortCoursesByDate(
          filteredCourses,
          'courseInfo.classroomProgress.createdAt',
          filter.sort === SORT_FILTER.LATEST
        );
      case SORT_FILTER.PROGRESS:
        return sortCoursesByProgress(filteredCourses);
      case SORT_FILTER.NAME_ASC:
      case SORT_FILTER.NAME_DESC:
        return sortCoursesByName(filteredCourses, filter.sort === SORT_FILTER.NAME_DESC);
      default:
        return filteredCourses;
    }
  };

  const { debouncedSearchTerm } = useSearchMyCourseProvider();

  const getSearchMyCourses = (courses) => {
    if (debouncedSearchTerm !== '') {
      const filterMyCourses = filterCourses(courses);
      const searchFilters = getFilteredItems(filterMyCourses, debouncedSearchTerm);
      const searchResults = searchFilters.map((item) => item.item);

      return searchResults;
    }

    return courses;
  };

  const scrollX = (offset) => {
    subjectListRef.current.scrollLeft += offset;
  };

  const scrollSubjectIntoView = (index) => {
    subjectRef.current[index].scrollIntoView({ block: 'nearest', inline: 'start' });
  };

  const handleScroll = () => {
    const maxSubjectListoffset =
      subjectListRef.current?.scrollWidth - subjectListRef.current?.offsetWidth;
    const currentOffset = subjectListRef.current.scrollLeft;
    if (currentOffset <= 50) {
      setScrollState(SCROLL_STATE.START);
    } else if (currentOffset + 50 >= maxSubjectListoffset) {
      setScrollState(SCROLL_STATE.END);
    } else {
      setScrollState(SCROLL_STATE.MIDDLE);
    }
  };

  if (!isSync) {
    return (
      <Flex mt={8}>
        <Loading />
      </Flex>
    );
  }

  return (
    <QueryComponent
      query={myCourses}
      variables={{ language: i18n.language, status, mode: MY_COURSES_MODE.ALL }}
      fetchPolicy="cache-and-network"
    >
      {(data) => {
        const courses = data.myCourses;
        const hasSearchText = debouncedSearchTerm !== '';
        const searchResults = getSearchMyCourses(courses);
        const subjectList = setSubjectList(searchResults);
        const currentSubjectCourses = getCurrentSubjectCourses(searchResults);
        const filteredCourses = filterCourses(currentSubjectCourses);

        // For the handle change tab then currentSubjectCourses have not course
        if (currentSubject !== DEFAULT_SUBJECT && currentSubjectCourses.length === 0) {
          setCurrentSubject(DEFAULT_SUBJECT);
        }

        const filteredCoursesText =
          filter.courses === COURSE_FILTER.CURRENT_COURSES
            ? t('COURSE_LIST.TITLE_CURRENT')
            : t('COURSE_LIST.TITLE_EXPIRED');

        const titleSearchResults = `${searchResults.length} ${t('COURSE_LIST.SEARCH_RESULT')}`;
        const titleFilteredCourses = `${filteredCourses.length} ${filteredCoursesText}`;
        const titleText = hasSearchText ? titleSearchResults : titleFilteredCourses;

        return (
          <>
            {myCoursesConfig?.enableCourseSubjectFilter && (
              <SubjectListContainer>
                {showArrow && (
                  <>
                    {scrollState !== SCROLL_STATE.START && (
                      <ArrowButton onClick={() => scrollX(-120)} isLeft={true}>
                        <SvgIcon icon={SvgIcon.ICON_LIST.BACK} size={24} color={'text'} />
                      </ArrowButton>
                    )}
                    {scrollState !== SCROLL_STATE.END && (
                      <ArrowButton onClick={() => scrollX(120)}>
                        <SvgIcon icon={SvgIcon.ICON_LIST.ARROW_RIGHT} size={24} color={'text'} />
                      </ArrowButton>
                    )}
                  </>
                )}

                <Flex
                  onScroll={handleScroll}
                  ref={subjectListRef}
                  ml={showArrow && scrollState !== SCROLL_STATE.START ? '50px' : 0}
                  mr={showArrow && scrollState !== SCROLL_STATE.END ? '50px' : 0}
                  style={{ overflowX: 'scroll' }}
                >
                  {subjectList.map((subject, index) => (
                    <SubjectWithRef
                      ref={(ref) => {
                        subjectRef.current[index] = ref;
                      }}
                      key={subject}
                      onClick={() => {
                        scrollSubjectIntoView(index);
                        logSubjectSelect(subject);
                        setCurrentSubject(subject);
                      }}
                      active={currentSubject === subject}
                    >
                      <Text color={currentSubject === subject ? 'primary' : 'text'}>
                        {subjects[subject]}
                      </Text>
                    </SubjectWithRef>
                  ))}
                </Flex>
              </SubjectListContainer>
            )}
            <CourseListView
              title={titleText}
              sortLabel={t('COURSE_LIST.FILTER')}
              onFilterChange={(filters) => {
                const newFilter = getFilterFromArray(filters);
                onFilterChange(newFilter);
                logFilterChange(`${newFilter.course} | ${newFilter.sort}`);
              }}
              filterOptions={options}
              searchResults={searchResults}
              selectedFilters={Object.values(filter)}
              courses={filteredCourses}
              onCourseCardClick={onCourseCardClick}
              expiredCoursesCount={getAvailableCourses(courses, true).length}
              pb={7}
              {...props}
            />
          </>
        );
      }}
    </QueryComponent>
  );
};

CourseList.propTypes = {
  status: PropTypes.string,
};

CourseList.defaultProps = {
  status: 'ACTIVE',
};

export default compose(withTranslation('products'), withApollo)(CourseList);
